import { z } from "zod";

import { PRODUCT_FEATURE_AREAS, type ProductUsageFilters } from "@signalist/shared-types";

const count = z.coerce.number().finite().nonnegative();
const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const period = z.union([z.literal(7), z.literal(30), z.literal(90)]);
const allPeriod = z.union([period, z.literal(365)]);
const surface = z.object({ web: count, extension: count, mcp: count, api: count });
const feature = z.object({
	key: z.string(),
	label: z.string(),
	area: z.enum(PRODUCT_FEATURE_AREAS),
	featureCode: z.string().nullable(),
});
const freshness = {
	lastRolledUpDate: date.nullable(),
	window: z
		.object({ from: date, to: date, days: count, previousFrom: date, previousTo: date })
		.nullable(),
};
const coverage = { distinctUsersDates: z.array(date), distinctUsersDays: count };
const filters = z.object({
	period,
	planId: count.nullable(),
	includeTrialDemo: z.boolean(),
	includeInternal: z.boolean(),
});
const cohort = z.object({
	kind: z.enum(["paying", "paying-trial-demo"]),
	size: count,
	description: z.string(),
	excludedCompanyIds: z.array(count),
	staffExcluded: z.literal(true),
	plans: z.array(z.object({ planId: count, name: z.string(), companies: count })),
});
const company = z.object({ companyId: count, name: z.string() });
const areas = <T extends z.ZodTypeAny>(row: T) =>
	z.array(z.object({ area: z.enum(PRODUCT_FEATURE_AREAS), features: z.array(row) }));

export const AdoptionResponseSchema = z.object({
	...freshness,
	...coverage,
	filters,
	cohort,
	catalogSize: count,
	areas: areas(
		feature.extend({
			companiesUsed: count,
			adoption: count,
			events: count,
			previousEvents: count,
			trend: z.discriminatedUnion("kind", [
				z.object({ kind: z.literal("change"), percent: z.coerce.number().finite() }),
				z.object({ kind: z.literal("new") }),
				z.object({ kind: z.literal("none") }),
			]),
			avgDailyDistinctUsers: count.nullable(),
			surfaces: z.object({ events: surface, shares: surface }),
		})
	),
});
export const UnusedResponseSchema = z.object({
	...freshness,
	filters,
	cohort,
	nearZeroAdoptionShare: count,
	areas: areas(
		feature.extend({
			reasons: z.array(z.enum(["zero", "near-zero", "flag-on-never-used"])),
			companiesUsed: count,
			adoption: count,
			events: count,
			flagOnCompanies: count.nullable(),
			flagOnNeverUsedCompanies: count,
			flagOnNeverUsedSample: z.array(company).max(10),
			lastEventDate: date.nullable(),
		})
	),
});
export const CompaniesResponseSchema = z.object({
	lastRolledUpDate: date.nullable(),
	companies: z.array(company.extend({ lastActivityDate: date })),
});
export const CompanyResponseSchema = z.object({
	...freshness,
	...coverage,
	period,
	company: company.extend({ analyticsEnabled: z.boolean() }),
	featuresUsed: count,
	catalogSize: count,
	lastActivityDate: date.nullable(),
	days: z.array(date),
	areas: areas(
		feature.extend({
			events: count,
			dailyEvents: z.array(count),
			dailyDistinctUsers: z.array(count.nullable()),
			avgDailyDistinctUsers: count.nullable(),
			lastEventDate: date.nullable(),
		})
	),
});
export const AllCustomersResponseSchema = z.object({
	...freshness,
	period: allPeriod,
	days: z.array(date),
	areas: areas(
		feature.extend({ events: count, surfaceEvents: surface, dailyEvents: z.array(count) })
	),
});

export const cohortParams = (filters: ProductUsageFilters) => ({
	period: filters.period,
	...(filters.planId === null ? {} : { planId: filters.planId }),
	includeTrialDemo: String(filters.includeTrialDemo),
	includeInternal: String(filters.includeInternal),
});
